import { UzDateTimePicker } from '@utazzo/ui';
import { useMemo } from 'react';

interface DateTimePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** ISO timestamp string (`YYYY-MM-DDTHH:mm:ss…Z`) or null when empty. */
  value: string | null;
  /** Emits the assembled ISO timestamp, or null when cleared. */
  onChange: (next: string | null) => void;
  /**
   * Earliest selectable date. Follow-up flows pass `new Date()` so
   * past days are locked but month-forward navigation stays open.
   */
  minDate?: Date;
  /** Latest selectable date. Optional - rarely needed for follow-ups. */
  maxDate?: Date;
}

/**
 * Thin adapter around `UzDateTimePicker` - keeps the parent's ISO-string API
 * (which matches the leads schema) while delegating the actual UI to a
 * single unified popover with calendar + time controls. See ADR-0032 §13.
 */
export const DateTimePicker = ({
  label,
  hint,
  error,
  required,
  disabled,
  value,
  onChange,
  minDate,
  maxDate,
}: DateTimePickerProps): React.JSX.Element => {
  const dateValue = useMemo<Date | null>(() => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [value]);

  return (
    <UzDateTimePicker
      label={label}
      hint={hint}
      error={error}
      required={required}
      disabled={disabled}
      value={dateValue}
      onValueChange={(d) => onChange(d ? d.toISOString() : null)}
      minDate={minDate}
      maxDate={maxDate}
      // 24-hour time (00-23), no AM/PM - matches the ops team's convention
      // and the Complete-Follow-up dialog which already uses 24h.
      hourFormat="24"
    />
  );
};
